"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { X, Cookie } from "lucide-react";

const STORAGE_KEY = "okpromo-cookie-consent";

type Consent = "accepted" | "rejected" | null;

/**
 * UK GDPR cookie consent banner.
 * - Renders fixed at the bottom-left until the user makes a choice
 * - Stores the choice in localStorage (key: okpromo-cookie-consent)
 * - Doesn't render at all until we've checked storage, so no flicker
 * - Accept + Reject + Privacy link, per ICO guidance (both options equal weight)
 *
 * NOTE: this is the UI layer only. Wire downstream analytics (GA4, etc.)
 * to read `localStorage.getItem('okpromo-cookie-consent') === 'accepted'`
 * before firing any non-essential tracking.
 */
export function CookieBanner() {
  const [consent, setConsent] = useState<Consent>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY) as Consent;
      setConsent(stored ?? null);
    } catch {
      setConsent(null);
    }
    setMounted(true);
  }, []);

  const decide = (choice: "accepted" | "rejected") => {
    try {
      localStorage.setItem(STORAGE_KEY, choice);
    } catch {
      // ignore (private mode etc.)
    }
    setConsent(choice);
  };

  // Don't render anything until we know the stored state — avoids flash
  if (!mounted || consent !== null) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      aria-live="polite"
      className="fixed bottom-4 left-4 right-4 sm:right-auto sm:max-w-md z-40 rounded-lg border border-border-strong bg-bg-soft shadow-soft p-4 sm:p-5 lg:p-6"
    >
      <div className="flex items-start gap-3">
        <Cookie className="size-5 mt-0.5 shrink-0 text-accent" aria-hidden="true" />
        <div className="flex-1">
          <p className="text-small font-semibold uppercase tracking-[0.18em] text-accent">
            Cookies
          </p>
          <p className="mt-2 text-body text-text-muted">
            We use essential cookies to make this site work, plus optional
            analytics to understand which content is useful. You can accept
            all, reject non-essentials, or read our{" "}
            <Link href="/privacy" className="text-accent link-anim">
              privacy policy
            </Link>
            .
          </p>
          <div className="mt-4 flex flex-col sm:flex-row sm:flex-wrap gap-2">
            <button
              type="button"
              onClick={() => decide("accepted")}
              className="inline-flex items-center gap-2 rounded-pill bg-accent px-4 py-2 text-small font-bold text-accent-ink transition hover:bg-accent-hover"
            >
              Accept all
            </button>
            <button
              type="button"
              onClick={() => decide("rejected")}
              className="inline-flex items-center gap-2 rounded-pill border border-border-strong bg-bg px-4 py-2 text-small font-semibold text-text transition hover:border-accent hover:text-accent"
            >
              Reject non-essentials
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={() => decide("rejected")}
          aria-label="Dismiss (treat as reject)"
          className="inline-flex size-7 items-center justify-center rounded-full text-text-muted transition hover:text-accent"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}
